import { TextField } from '@fluentui/react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  useState,
  useRef,
  useEffect,
  InputHTMLAttributes,
  RefAttributes,
  useCallback,
} from 'react';

interface IAddressPickerParams {
  style?: React.CSSProperties;
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  value?: string;
}

const AddressPicker = (params: IAddressPickerParams) => {
  return (
    <APIProvider
      apiKey={`${import.meta.env.VITE_GOOGLE_MAP_APIKEY}`}
      libraries={['marker', 'places']}
    >
      <div className="autocomplete-control">
        <PlaceAutocomplete
          style={params.style}
          onPlaceSelect={params.onPlaceSelect}
          value={params.value}
        />
      </div>
    </APIProvider>
  );
};

export default AddressPicker;

interface PlaceAutocompleteProps {
  style?: React.CSSProperties;
  onPlaceSelect: (place: google.maps.places.PlaceResult | null) => void;
  value?: string;
}

const PlaceAutocomplete = ({
  style,
  onPlaceSelect,
  value,
}: PlaceAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [placeAutocomplete, setPlaceAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  // Use a ref for the HTML input element
  const inputRef = useRef<HTMLInputElement>(null);
  const places = useMapsLibrary('places');

  // Sync inputValue with value prop when it changes
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  useEffect(() => {
    if (!places || !inputRef.current) return;

    const options = {
      fields: ['geometry', 'name', 'formatted_address'],
    };

    setPlaceAutocomplete(new places.Autocomplete(inputRef.current, options));
  }, [places]);

  useEffect(() => {
    if (!placeAutocomplete) return;

    const listener = placeAutocomplete.addListener('place_changed', () => {
      const place = placeAutocomplete.getPlace();
      if (place && place.formatted_address) {
        // Update local state to show the selected address
        setInputValue(place.formatted_address);
      } else {
        setInputValue('');
      }
      onPlaceSelect(placeAutocomplete.getPlace());
    });

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [onPlaceSelect, placeAutocomplete]);

  const onRenderInput = useCallback(
    (
      props?: InputHTMLAttributes<HTMLInputElement> &
        RefAttributes<HTMLInputElement>
    ) => {
      return (
        <input
          {...props}
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          autoComplete="off"
        />
      );
    },
    [inputValue]
  );

  return (
    <div className="autocomplete-container">
      <TextField style={style} onRenderInput={onRenderInput} />
    </div>
  );
};
